import { api } from "@/lib/api";
import { Category, CategoryStatus, CategoryTranslation } from "../schema";

export interface CreateCategoryData {
  title: string;
  primaryLanguageCode?: string;
  color?: string;
  thumbnailUrl?: string | null;
  translations?: {
    languageCode: string;
    isDefault: boolean;
    localizedTitle?: string;
    localizedDescription?: string;
  }[];
}

export interface UpdateCategoryData {
  title?: string;
  status?: CategoryStatus;
  color?: string | null;
  thumbnailUrl?: string | null;
  primaryLanguageCode?: string;
}

export interface CategoryTranslationInput {
  languageCode: string;
  isDefault: boolean;
  localizedTitle?: string;
  localizedDescription?: string;
}

export const categoryService = {
  async findAll(filters?: {
    search?: string;
    status?: CategoryStatus;
  }): Promise<Category[]> {
    const params = new URLSearchParams();
    if (filters?.search) params.set("search", filters.search);
    if (filters?.status) params.set("status", filters.status);

    return api.get<Category[]>(`/content-category?${params.toString()}`);
  },

  async findOne(id: string): Promise<Category> {
    return api.get<Category>(`/content-category/${id}`);
  },

  async create(data: CreateCategoryData): Promise<Category> {
    return api.post<Category>("/content-category", data);
  },

  async update(id: string, data: UpdateCategoryData): Promise<Category> {
    return api.patch<Category>(`/content-category/${id}`, data);
  },

  async archive(id: string): Promise<void> {
    return api.delete(`/content-category/${id}`);
  },

  async restore(id: string): Promise<Category> {
    return api.post<Category>(`/content-category/${id}/restore`, {});
  },

  async getTranslations(id: string): Promise<CategoryTranslation[]> {
    return api.get<CategoryTranslation[]>(
      `/content-category/${id}/translations`,
    );
  },

  async addTranslation(
    id: string,
    data: CategoryTranslationInput,
  ): Promise<CategoryTranslation> {
    return api.post<CategoryTranslation>(
      `/content-category/${id}/translations`,
      data,
    );
  },

  async updateTranslation(
    translationId: string,
    data: Partial<CategoryTranslationInput>,
  ): Promise<CategoryTranslation> {
    return api.patch<CategoryTranslation>(
      `/content-category/translations/${translationId}`,
      data,
    );
  },

  async deleteTranslation(translationId: string): Promise<void> {
    return api.delete(`/content-category/translations/${translationId}`);
  },
};
